import type { MetaHeadProps } from './types';
import type { ReactElement } from 'react';

import { useRouter } from 'next/compat/router';

import { BaseMetaHead } from './BaseMetaHead';
import { useAppContext } from '../../../utilities/context/static/AppContext';

const campaignRegEx = /(^\/\(sites\)\/\[siteType\]\/campaign)|(\?.*)|(#.*)/g;
const slugCampaignRegEx = /(^\/\(sites\)\/\[siteType\]\/campaign)(\/[^/]*)/;

export const CampaignMetaHead = ({
  metadataContent,
  slug,
}: MetaHeadProps & { slug?: string }): ReactElement => {
  const router = useRouter();
  const { locale, productionHost } = useAppContext();

  const path =
    slug !== undefined
      ? `${router?.route.match(slugCampaignRegEx)?.[2] || ''}${slug.length ? `/${slug}` : ''}`
      : router?.route.replace(campaignRegEx, '') ?? '';

  const getMetaTagContent = (name: string): string | undefined =>
    metadataContent?.metaTags?.find(tag => tag.name === name)?.content;

  const baseUrl = getMetaTagContent('og:url') || `${productionHost}${path}`;

  const iosDeepLink = `gstar://contentextension?url=${Buffer.from(baseUrl).toString(
    'base64'
  )}&title=${encodeURI('G-STAR')}`;

  return (
    <BaseMetaHead
      title={metadataContent?.pageTitle || ''}
      description={metadataContent?.pageDescription || ''}
      robots={metadataContent?.robots}
      canonicalUrl={`${productionHost}/${locale}${path}`}
      metaTags={metadataContent?.metaTags}
      showTwitterCard={getMetaTagContent('twitter:card') === undefined}
      twitterSiteContent="@GStarRAW"
      iosDeepLink={iosDeepLink}
      universalLinksRequireGStar
      ogImageChildren={
        getMetaTagContent('og:image') === undefined &&
        metadataContent?.ogImageUrl && (
          <>
            <meta property="og:image" content={metadataContent?.ogImageUrl} />
            {getMetaTagContent('og:image:width') === undefined && (
              <meta property="og:image:width" content="1200" />
            )}
            {getMetaTagContent('og:image:height') === undefined && (
              <meta property="og:image:height" content="630" />
            )}
          </>
        )
      }
    >
      <meta property="og:type" content="website" />
      {getMetaTagContent('og:url') === undefined && <meta property="og:url" content={baseUrl} />}
      {getMetaTagContent('og:title') === undefined && (
        <meta property="og:title" content={metadataContent?.pageTitle || ''} />
      )}
      {getMetaTagContent('og:description') === undefined && metadataContent?.pageDescription && (
        <meta property="og:description" content={metadataContent?.pageDescription || ''} />
      )}
    </BaseMetaHead>
  );
};
